import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Link } from 'gatsby'
import { kebabCase } from 'lodash'
import { Box, Typography, Icon, IconButton, Paper } from '@material-ui/core'
import ButtonBase from '@material-ui/core/ButtonBase'
import ClearIcon from '@material-ui/icons/Clear'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import StakeInput from './StakeInput'
import CashOut from './CashOut'
import { formatPrice } from '../../utils/helpers'

const Base = styled(Paper)`
  position: relative;
  margin: ${props => props.theme.spacing(3)}px;
`
const BetSelection = styled(Typography)`
  padding-right: ${props => props.theme.spacing(8)}px;
`
const DateReturns = styled(Box)`
  align-items: center;
`
const MarketEvent = styled.div`
  flex: 1;
  overflow: hidden;
`
const BetCardInputs = styled.div`
  width: 100px;
  margin-left: 8px;
`
const BetType = styled(Typography)`
  width: 100%;
`
const BetSlug = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  margin-right: ${props => props.theme.spacing(1)}px;
`
const BetDate = styled(Typography)`
  width: 100%;
`
const BetReturns = styled(Typography)`
  text-align: right;
  width: 100%;
`
const BetStake = styled(Typography)`
  text-align: right;
  margin-left: auto;
  white-space: nowrap;
`
const MultiLegs = styled(Typography)`
  margin-right: ${props => props.theme.spacing(2)}px;
  width: 100%;
`
const RemoveBetButton = styled(IconButton)`
  position: absolute;
  top: -${props => props.theme.spacing(1)}px;
  right: 0;
`
const FreeBet = styled(Box)`
  border: 0;
  border-top: 1px solid;
  border-color: ${props => props.theme.palette.secondary.main};
  overflow: hidden;
  cursor: pointer;
`

const BetCard = props => {
  const {
    disabled,
    id,
    sport,
    competition,
    marketTitle,
    name,
    slug,
    multiLegs,
    odds,
    date,
    start,
    freebet,
    clickHandler,
    inputHandler,
    returns,
    stake,
    editable,
    cashout,
    betKey,
    betDetails,
  } = props

  return (
    <Base className={editable ? `is-editable` : null}>
      <Box py={3} px={4}>
        <Box display="flex">
          <BetSelection variant="subtitle1" color="textPrimary">
            {name} @ {parseFloat(odds).toFixed(2)}
          </BetSelection>
          {editable && (
            <RemoveBetButton onClick={clickHandler}>
              <ClearIcon />
            </RemoveBetButton>
          )}
        </Box>
        <Box display="flex" alignItems="center" width={1}>
          <MarketEvent>
            <BetType
              variant={editable ? 'body2' : 'subtitle2'}
              color="textSecondary"
            >
              {marketTitle}
            </BetType>
            <Box display="flex" alignItems="center" width={1}>
              {slug && (
                <Typography variant="body2" color="primary">
                  <BetSlug
                    to={`/${kebabCase(sport)}/${kebabCase(
                      competition
                    )}/${kebabCase(slug)}-${kebabCase(id)}/`}
                  >
                    {slug}
                  </BetSlug>
                </Typography>
              )}
              {multiLegs && (
                <MultiLegs variant="body2" color="textSecondary" noWrap>
                  {multiLegs}
                </MultiLegs>
              )}
              {!editable && (
                <BetStake variant="body2" color="textPrimary">
                  Stake: {formatPrice(stake * 100)}
                </BetStake>
              )}
            </Box>
          </MarketEvent>
          {editable && (
            <BetCardInputs>
              <StakeInput
                stake={stake}
                inputHandler={inputHandler}
                disabled={disabled}
              />
            </BetCardInputs>
          )}
        </Box>
        <DateReturns display="flex" width={1} pt={1}>
          <BetDate variant="body2" color="textSecondary">
            {date} {start}
          </BetDate>
          <BetReturns variant="body2" color="textSecondary" noWrap>
            Returns: {formatPrice(returns * 100)}
          </BetReturns>
        </DateReturns>
        {cashout && <CashOut betDetails={betDetails} betKey={betKey} />}
      </Box>

      {freebet && (
        <ButtonBase
          style={{
            width: '100%',
          }}
        >
          <FreeBet display="flex" alignItems="center" width={1} p={3} px={4}>
            <Typography variant="body2" color="primary">
              Use free bet
            </Typography>
            <Icon
              color="primary"
              style={{
                margin: '-6px -4px -6px auto',
              }}
            >
              <ExpandMoreIcon />
            </Icon>
          </FreeBet>
        </ButtonBase>
      )}
    </Base>
  )
}

BetCard.propTypes = {
  disabled: PropTypes.bool,
  id: PropTypes.string,
  sport: PropTypes.string,
  competition: PropTypes.string,
  marketTitle: PropTypes.string,
  odds: PropTypes.any,
  start: PropTypes.string,
  name: PropTypes.string,
  date: PropTypes.string,
  slug: PropTypes.string,
  multiLegs: PropTypes.array,
  clickHandler: PropTypes.func,
  freebet: PropTypes.bool,
  inputHandler: PropTypes.func,
  returns: PropTypes.number,
  stake: PropTypes.number,
  editable: PropTypes.bool,
  cashout: PropTypes.bool,
  betKey: PropTypes.string,
  betDetails: PropTypes.object,
}

export default BetCard
